import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import {
  ChildSettlementGroupModel,
  ChildSettlementGroupRequestModelDTO,
  NewSettlementGroupContractModel,
  SettlementGroup,
  SettlementGroupAggregatedEditBatchRequest,
  SettlementGroupContractMoveRequest,
  SettlementGroupPatchRequest,
  SettlementGroupPostRequest,
  SettlementGroupTable
} from '../../api/Argentia/endpoints/settlementGroups/model/settlementGroups'
import { EnumTipoProrrateo } from '../../api/Argentia/enums/tipoProrrateo'
import { EnumTypeSettlementGroup } from '../../api/Argentia/enums/typeSettlementGroup'
import {
  CountryModel,
  MunicipalityModel,
  PostalAddress,
  PostalCode,
  ProvinceModel
} from '../../api/Argentia/model/addresses'
import { PaymentMethodTypeModel } from '../../api/Argentia/model/contracts'
import { EnumTipoMetodoPago } from '../../api/oData/enums/enumTipoMetodoPago'
import { enumEnumToValueNumber, enumKeyToValue, enumValueToKey } from '../../helpers'
import { getResourceUserPermissions } from '../../helpers/permissionsHelper'
import { Resources } from '../../model/RBAC/constants'
import { VirtualBatteryDetailPage } from '../floatingConcepts/virtualBatteryDetail/virtualBatteryDetail'
import { SettlementGroupAggregatedAndVirtualAddFormDataType } from './aggregatedAdd/settlementGroup.aggregatedAdd'
import { SettlementGroupAggregatedEditFormDataType } from './aggregatedEdit/settlementGroup.aggregatedEdit'
import { SettlementGroupAddDefinition } from './components/settlementGroupsTable'
import {
  ChildSettlementGroupTableData,
  EnumSettlementGroupMoveTo,
  FormAddChildSettlementGroup,
  SetllementGroupContractRow,
  SettlementGroupAggregatedEditFormIsDirty,
  SettlementGroupVirtualEditFormDataType
} from './settlementGroups.model'

export const settlementGroupsToTableModelMapper = (
  settlementGroups: SettlementGroup[]
): SettlementGroupTable[] => {
  const hasExtendedClientPermissions =
    getResourceUserPermissions(Resources.ExtendedClients)?.length > 0

  return settlementGroups.map(sg =>
    settlementGroupItemToTableModelMapper(sg, hasExtendedClientPermissions)
  )
}

export const settlementGroupItemToTableModelMapper = (
  settlementGroup: SettlementGroup,
  hasExtendedClientPermissions: boolean
): SettlementGroupTable => {
  return {
    ...settlementGroup,
    typeSettlementGroup: settlementGroup.typeSettlementGroup,
    flexibilitiesQty: settlementGroup.flexibilities
      ? settlementGroup.flexibilities.length.toString()
      : '0',
    contractsQty: (
      (settlementGroup.contracts?.length ?? 0) +
      (settlementGroup.subjectList?.length ?? 0) +
      (
        settlementGroup.typeSettlementGroup == EnumTypeSettlementGroup.GrupoEmpresarial ||
        settlementGroup.typeSettlementGroup == EnumTypeSettlementGroup.FlexibilidadesGrupales
          ? (settlementGroup.childSettlementGroups?.length ?? 0)
          : 0
      )
    ).toString(),
    // ...(settlementGroup.contracts?.length > 0 && { order: settlementGroup.contracts }),
    flexibilitiesCanDelete:
      settlementGroup.flexibilities.length === 0 && settlementGroup.contracts.length === 0
        ? true
        : false,
    contractsCanDelete: settlementGroup.contracts.length === 0 ? true : false,
    ...(settlementGroup.subject && {
      subject: {
        id: settlementGroup.subject.id,
        description: settlementGroup.subject.description,
        subtype: settlementGroup.subject.subtype,
        personType: settlementGroup.subject.personType,
        documentType: settlementGroup.subject.documentType,
        documentID: settlementGroup.subject.documentID,
        name: settlementGroup.subject.name ?? '',
        surname1: settlementGroup.subject.surname1,
        surname2: settlementGroup.subject.surname2,
        businessName: settlementGroup.subject.businessName,
        fiscalAddress: settlementGroup.subject.fiscalAddress,
        contactInfo: settlementGroup.subject.contactInfo,
        language: settlementGroup.subject.language,
        companyID: settlementGroup.subject.companyID,
        state: settlementGroup.subject.state
      }
    }),
    ...(settlementGroup.paymentMethod && {
      paymentMethod: settlementGroup.paymentMethod
    }),
    hasExtendedClientPermissions: hasExtendedClientPermissions
  }
}

export const settlementGroupEditMapper = (
  settlementGroup: SettlementGroupTable
): SettlementGroupPatchRequest => {
  return {
    ...settlementGroup,
    typeSettlementGroup: enumEnumToValueNumber(
      EnumTypeSettlementGroup,
      settlementGroup.typeSettlementGroup
    ),
    apportionment: enumEnumToValueNumber(EnumTipoProrrateo, settlementGroup.apportionment)
  }
}

export const consolidateSettlementGroupsMapper = (groups: SettlementGroup[]): SettlementGroup[] => {
  return groups.map(group => ({
    ...group,
    typeSettlementGroup: enumEnumToValueNumber(EnumTypeSettlementGroup, group.typeSettlementGroup)!
  }))
}

export const mapFromPostalAdress = (postalAddress: PostalAddress) => {
  const newMunicipality: MunicipalityModel = {
    id: postalAddress.municipality.id,
    code: postalAddress.municipality.code,
    description: postalAddress.municipality.description,
    autonomyCode: postalAddress.municipality.autonomyCode,
    latitude: postalAddress.municipality.latitude,
    logitude: postalAddress.municipality.logitude,
    provinceID: postalAddress.municipality.provinceID
  }
  const newProvince: ProvinceModel = {
    id: postalAddress.province.id,
    description: postalAddress.province.description,
    code: postalAddress.province.code,
    postalCodeDesc: postalAddress.province.postalCodeDesc,
    countryID: postalAddress.province.countryID
  }
  const newCountry: CountryModel = {
    id: postalAddress.country.id,
    description: postalAddress.country.description,
    code: postalAddress.country.code
  }
  const newPostalCode: PostalCode = {
    id: postalAddress.id,
    postalCode: postalAddress.postalCode,
    municipality: newMunicipality
  }

  return { newPostalCode, newMunicipality, newProvince, newCountry }
}

export const postalCodeListMapper = (items: PostalCode[]): SelectOption[] => {
  return items ? items.map(postalCodeItemMapper) : []
}

export const postalCodeItemMapper = (item: PostalCode): SelectOption => {
  return (
    item && {
      value: item.id,
      label: `${item.postalCode} - ${item.municipality?.description}`
    }
  )
}

export const paymentMethodTypesToOptionsMapper = (
  methods: PaymentMethodTypeModel[]
): SelectOption[] => {
  return methods && methods.map(paymentMethodTypeMapper)
}

export const paymentMethodTypeMapper = (method: PaymentMethodTypeModel): SelectOption => {
  return (
    method && {
      value: method.Id,
      label: method.description
    }
  )
}

export const paymentMethodMapper = (
  methodId: string | number,
  methods: PaymentMethodTypeModel[]
): PaymentMethodTypeModel => {
  return methods && methods.find(m => m.Id === +methodId)!
}

export const mapSettlementGroupToEditFormData = (
  settlementGroup: SettlementGroupTable
): SettlementGroupAggregatedEditFormDataType => {
  return {
    //header section
    id: settlementGroup.id,
    code: settlementGroup.code,
    description: settlementGroup.description,
    typeSettlementGroup: settlementGroup.typeSettlementGroup,
    subjectId: settlementGroup.subject?.id,
    subject: settlementGroup.subject?.description,
    //payment section
    ...(settlementGroup.paymentMethod && {
      paymentMethodId: settlementGroup.paymentMethod.Id,
      paymentMethod: enumValueToKey(EnumTipoMetodoPago, settlementGroup.paymentMethod.type),
      iban: settlementGroup.paymentMethod?.iban,
      //Billing address -> it is associated to paymentMethod existence
      addressId: settlementGroup.paymentMethod.address.Id,
      street: settlementGroup.paymentMethod.address.postalAddress,
      postalCode: settlementGroup.paymentMethod.address.postalCode,
      postalCodeSearch: {
        value: settlementGroup.paymentMethod.address.postalCode,
        label: settlementGroup.paymentMethod.address.postalCode
      },
      municipality: settlementGroup.paymentMethod.address.municipality,
      province: settlementGroup.paymentMethod.address.province,
      country: settlementGroup.paymentMethod.address.country,
      municipalityId: settlementGroup.paymentMethod.address.municipalityId,
      provinceId: settlementGroup.paymentMethod.address.provinceId,
      countryId: settlementGroup.paymentMethod.address.countryId
    })
    // ...(settlementGroup.subject && {
    //   subject: settlementGroup.description,
    //   subjectId: settlementGroup.subject.id
    // }),
    // ...(settlementGroup.contracts && {
    //   contracts: settlementGroup.contracts.map(c => c.contractId)
    // }),
  }
}

export const mapSettlementGroupEditToBatchRequest = (
  formData: SettlementGroupAggregatedEditFormDataType,
  editedSection: SettlementGroupAggregatedEditFormIsDirty
): SettlementGroupAggregatedEditBatchRequest => {
  let request: SettlementGroupAggregatedEditBatchRequest = {}
  let header = {}
  let paymentMethod = {}
  let address = {}

  if (editedSection.header) {
    header = {
      settlementGroup: {
        id: formData.id,
        description: formData.description,
        code: formData.code,
        typeSettlementGroup: formData.typeSettlementGroup
      }
    }
  }
  if (editedSection.payment) {
    paymentMethod = {
      paymentMethod: {
        Id: formData.paymentMethodId,
        type: formData.paymentMethod,
        iban: formData.iban === '' || formData.iban === undefined ? null : formData.iban
      }
    }
  }
  if (editedSection.billingAddress) {
    address = {
      address: {
        Id: formData.addressId,
        postalAddress: formData.street,
        postalCode: formData.postalCode,
        municipalityId: formData.municipalityId,
        provinceId: formData.provinceId,
        countryId: formData.countryId
      }
    }
  }

  request = { ...header, ...paymentMethod, ...address }

  return request
}
export const mapSettlementGroupToRequest = (
  newGroup: SettlementGroupAggregatedAndVirtualAddFormDataType
): SettlementGroupPostRequest => {
  let mappedSettlementGroup: SettlementGroupPostRequest = {
    code: newGroup.code!,
    description: newGroup.description!,
    typeSettlementGroup: +newGroup.typeSettlementGroup!,
    ...(newGroup.subject && {
      subject: {
        id: newGroup.subject?.value as number
      }
    }),
    contracts: mapSettlementGroupsContractsToRequest(
      newGroup.contracts,
      newGroup.typeSettlementGroup!,
      newGroup.startDate!,
      newGroup.endDate
    ),
    ...(newGroup.paymentMethod && {
      paymentMethod: {
        type: +newGroup.paymentMethod!,
        ...(newGroup.iban && { iban: newGroup.iban! }),
        address: {
          postalAddress: newGroup.street!,
          postalCode: newGroup.postalCode!,
          municipalityId: newGroup.municipalityId!,
          provinceId: newGroup.provinceId!,
          countryId: newGroup.countryId!
        }
      }
    })
  }
  return mappedSettlementGroup
}

export const mapSettlementGroupToPostRequest = (
  newGroup: SettlementGroupAddDefinition
): SettlementGroupPostRequest => {
  let mappedSettlementGroup: SettlementGroupPostRequest = {
    code: newGroup.code!,
    description: newGroup.description!,
    typeSettlementGroup: +newGroup.typeSettlementGroup!,
    apportionment: newGroup.apportionment,
    ...(newGroup.subject && {
      subject: {
        id: newGroup.subject?.value as number
      }
    }),
    contracts:
      newGroup.typeSettlementGroup === EnumTypeSettlementGroup.BateriaVirtual
        ? mapSettlementGroupsContractsToRequest(
            newGroup.contracts,
            newGroup.typeSettlementGroup!,
            newGroup.startDate!,
            newGroup.endDate
          )
        : []
  }
  return mappedSettlementGroup
}

export const mapSettlementGroupToPatchRequest = (
  editedGroup: SettlementGroupVirtualEditFormDataType
): SettlementGroupPatchRequest => {
  let mappedSettlementGroup: SettlementGroupPatchRequest = {
    id: editedGroup.id,
    code: editedGroup.code,
    description: editedGroup.description!
  }
  return mappedSettlementGroup
}

export const mapSettlementGroupEditToPatchRequest = (
  editedGroup: SettlementGroupAddDefinition
): SettlementGroupPatchRequest => {
  let mappedSettlementGroup: SettlementGroupPatchRequest = {
    id: editedGroup.id!,
    code: editedGroup.code!,
    description: editedGroup.description!,
    apportionment: editedGroup.apportionment
  }
  return mappedSettlementGroup
}

export const mapSettlementGroupsContractsToRequest = (
  newContracts: number[],
  newSettlementGroupType: EnumTypeSettlementGroup,
  startDate: Date,
  endDate?: Date
): NewSettlementGroupContractModel[] => {
  const parseStartDate = moment(startDate).format('YYYY-MM-DD')
  const parseEndDate = endDate ? moment(endDate).format('YYYY-MM-DD') : null

  const contracts =
    newSettlementGroupType === EnumTypeSettlementGroup.BateriaVirtual
      ? newContracts.slice().sort((a, b) => a - b)
      : newContracts

  return contracts.map((contractId, index) => {
    let contractModel: NewSettlementGroupContractModel = {
      contractId: Number(contractId),
      startDate: parseStartDate,
      endDate: parseEndDate
    }

    if (newSettlementGroupType === EnumTypeSettlementGroup.BateriaVirtual) {
      contractModel.order = index + 1
    }

    return contractModel
  })
}

export const mapFlexibilitiesTableToPatchRequest = (
  group: SettlementGroupTable
): SettlementGroupPatchRequest => {
  return {
    id: group.id,
    description: group.description,
    code: group.code,
    typeSettlementGroup: group.typeSettlementGroup,
    apportionment: group.apportionment
  }
}

export const mapRowToMoveSettlementGroupContractRequest = (
  row: SetllementGroupContractRow,
  settlementGroupId: number,
  moveTo: EnumSettlementGroupMoveTo
): SettlementGroupContractMoveRequest => {
  return {
    contractId: row.contractId,
    settlementGroupId: settlementGroupId,
    moveTo: moveTo
  }
}

export const mapSettlementGroupTableToVirtualDetail = (
  group: SettlementGroupTable
): VirtualBatteryDetailPage => {
  return {
    subjectId: group.subject?.id ?? 0,
    subjectDescription: group.subject?.description ?? '',
    settlementGroupId: group.id,
    settlementGroupDescription: group.code,
    codeColumn: group.code
  }
}

export const mapSettlementGroupOptionsList = (response: SettlementGroup[]): SelectOption[] => {
  return response.map(item => {
    return {
      value: item.id,
      label: item.description
    }
  })
}

export const mapChildSettlementGroupTableData = (
  data: ChildSettlementGroupModel[]
): ChildSettlementGroupTableData[] => {
  return data.map(item => {
    return {
      ...item,
      childGroupCode: item.childSettlementGroup?.code,
      childGroupDescription: item.childSettlementGroup?.description,
      childType: item.childSettlementGroup?.typeSettlementGroup
    }
  })
}

export const mapChildSettlementGroupFormDataToRequest = (
  formData: FormAddChildSettlementGroup,
  parentGroupId: number
): ChildSettlementGroupRequestModelDTO => {
  return {
    parentSettlementGroupId: parentGroupId,
    childSettlementGroupId: formData.childSettlementGroupId,
    startDate: moment(formData.vinculationStartDate).format('YYYY-MM-DD'),
    endDate: formData.vinculationEndDate
      ? moment(formData.vinculationEndDate).format('YYYY-MM-DD')
      : null
  }
}
